import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import { SectionHeader } from "@/components/common/section-header";
import { CountdownTimerSection } from "@/types/dto/landing-page/landing-page";
import { alpha, Box, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React from "react";
import Image from "next/image";
import LiveCountdown from "./live-countdown";

export interface CountdownTimerProps {
  countdownTimer: CountdownTimerSection;
  moduleRankingPosition?: string;
  moduleId?: string;
}

const CountdownTimer = ({ countdownTimer, moduleRankingPosition, moduleId }: CountdownTimerProps) => {
  const ctas = countdownTimer.ctaButtons;
  const endTime = new Date(countdownTimer.countdownEndDatetime).getTime();
  const isFirstModule = moduleRankingPosition === "1";

  return (
    <Stack
      className="fluid-box"
      data-testid="countdown-timer"
      data-track="visibility"
      data-parent-container-type="Landing Page"
      data-module-name="Countdown Timer"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      data-module-id={moduleId}
      sx={{
        position: "relative",
        isolation: "isolate",
        backgroundColor: countdownTimer.backgroundImage.imageUrl ? "grey.300" : "common.black"
      }}
    >
      {countdownTimer.backgroundImage.imageUrl && (
        <>
          <Image
            data-testid="countdown-timer-background-image"
            src={countdownTimer.backgroundImage.imageUrl}
            alt=""
            aria-hidden="true"
            fill
            sizes="100vw"
            priority={isFirstModule}
            style={{ objectFit: "cover", objectPosition: "center", zIndex: -2 }}
          />
          <Box
            data-testid="countdown-timer-background-overlay"
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: -1,
              pointerEvents: "none",
              backgroundImage: (theme) =>
                `linear-gradient(0deg, ${alpha(theme.palette.common.black, 0.6)} 0%, ${alpha(theme.palette.common.black, 0)} 100%)`
            }}
          />
        </>
      )}
      <Stack
        className="full-width-container"
        data-testid="countdown-timer-content"
        sx={{
          padding: { xs: "4rem 2.4rem", md: "4rem 3.2rem", lg: "4rem 10rem" },
          minHeight: {xs: "100%", sm: "60rem"},
          alignItems: "center",
          width: "100%",
          justifyContent: "center",
          gap: "2.4rem"
        }}
      >
        <Stack
          data-testid="countdown-timer-lockup"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Typography
            variant="h1"
            component="h1"
            data-testid="countdown-timer-lockup-text"
            sx={{
              color: "common.white",
              textAlign: "center",
              fontSize: { xs: "3.8rem", md: "4rem", lg: "4.8rem" },
              fontStyle: "normal",
              fontWeight: 800,
              lineHeight: { xs: "4.6rem", md: "4.8rem", lg: "6rem" },
              letterSpacing: "-0.05rem",
              ...(countdownTimer.lockupType === "Image" ? visuallyHidden : {})
            }}
          >
            {countdownTimer.lockupText}
          </Typography>
          {countdownTimer.lockupType === "Image" && countdownTimer.lockupImage?.imageUrl && (
            <Box
              data-testid="countdown-timer-lockup-image-container"
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "70rem",
                aspectRatio: "700 / 196"
              }}
            >
              <Image
                data-testid="countdown-timer-lockup-image"
                src={countdownTimer.lockupImage.imageUrl}
                alt={countdownTimer.lockupImage.altText ?? ""}
                fill
                priority={isFirstModule}
                // 748px = 700px maximum image width + 48px total horizontal padding.
                sizes="(max-width: 748px) calc(100vw - 48px), 700px"
                style={{ objectFit: "contain", objectPosition: "bottom" }}
              />
            </Box>
          )}
        </Stack>
        <SectionHeader
          headerText={countdownTimer.headline}
          copyText={countdownTimer.supportingCopy}
          headerTestId="countdown-timer-headline"
          copyTestId="countdown-timer-supporting-copy"
          headerLevel="h6"
          sx={{ alignItems: "center" }}
          headerSx={{
            textAlign: "center",
            color: "primary.contrastText",
            fontSize: { xs: "1.6rem", lg: "1.8rem" },
            fontWeight: 600,
            lineHeight: { xs: "2.6rem", lg: "3rem" }
          }}
          copySx={{ textAlign: "center", color: "common.white" }}
        />
        {!Number.isNaN(endTime) && (
          <LiveCountdown endTime={endTime} isLight={countdownTimer.themeVariant === "Light"} />
        )}
        {ctas.length > 0 && (
          <Stack
            data-testid="countdown-timer-ctas"
            direction={{ xs: "column", md: "row" }}
            spacing="2.4rem"
            alignItems="center"
            justifyContent="center"
          >
            {ctas
              .filter((cta) => cta.label)
              .map((cta, index) => (
                <DynamicCtaButton
                  key={`${cta.ctaType}-${cta.label}`}
                  label={cta.label}
                  ctaType={cta.ctaType}
                  ctaData={cta.ctaData}
                  variant={cta.variant ?? "contained"}
                  color={cta.color ?? "primary"}
                  testId={`countdown-timer-${index}`}
                  targetIdSuffix={`countdown-timer-${index}`}
                />
              ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default CountdownTimer;
